import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import { UnifiedAlertsIcons } from './icons';
import PageWrapper from 'components/Common/PageWraper';
import SeverityTiles from './SeverityTiles/SeverityTiles';
import { SUB_TILES_BY_PARENT } from './constants';
import { TIME_RANGE_OPTIONS } from './AdvancedFilterDrawer';
import { buildFilters, DEFAULT_FILTERS, DOMAIN_MAP, FilterStateOps, getFiltersForTile, getItsmSortParams, getParentTileValue, PROGRAM_MAP, useUnifiedIncidents } from './hooks/useUnifiedIncidents';
import IncidentsTable, { buildReverseSectorMap, Incident, mapDomainValue } from './AlertsTable/IncidentsTable';
import AdvanceFilterOpsDrawer from './AdvancedFilterDrawer/AdvanceFilterOpsDrawer';
import ImagePreview from 'utils/ImagePreview';
import CaptureExportControls from 'utils/CaptureExportControls';
import MostImpactedApp from './TrendCharts/MostImpactedApp';
import TrendByProgram from './TrendCharts/TrendByProgram';
import TrendBySector from './TrendCharts/TrendBySector';
import { generateDashboardPdf } from 'utils/pdfExportUtil';
import type { PdfColumn } from 'utils/pdfExportUtil';
import ManageSopDialog, { ManageSopEntityConfig } from './ManageSOP/ManageSOPDialog';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { snowUrl } from 'service/config';
import * as unifiedAlertsService from 'service/unifiedAlerts';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import OpsAdvancedFilterPanel from './AdvancedFilterDrawer/OpsAdvancedFilterPanel';
import moment from 'moment-timezone';
import { TIMEZONE_MAP } from 'utils/common-util';
import { GenericOption } from 'components/Inputs/MultiSelectAutocomplete';

export const timestamp = new Date();
export const formattedDate = timestamp.toLocaleString('en-GB', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}).replace(/[/, ]/g, '_');

const CHART_CARDS = [
  { key: 0, label: 'Trends by Sector', sub: 'Last 30 Days', Icon: UnifiedAlertsIcons.TrendBySectorIcon },
  { key: 1, label: 'Trends by Program', sub: 'All Markets', Icon: UnifiedAlertsIcons.TrendByProgramIcon },
  { key: 2, label: 'Most Impacted Applications', sub: 'View All', Icon: UnifiedAlertsIcons.MostImpactedAppIcon },
];

const includeGroupByMenuColumns = ['assignment_group', 'program', 'sector', 'domain', 'u_source'];

const groupByList = [
  { label: 'Assignment Group', value: 'assignment_group' },
  { label: 'Program', value: 'program' },
  { label: 'Sector', value: 'sector' },
  { label: 'Domain', value: 'domain' },
  { label: 'Source', value: 'u_source' },
];

const SnowUnifiedIncidentsDashboard = () => {
  const theme = useTheme();
  const p = theme.palette;
  const { setRightContextContent } = useHeader();

  const [activeContent, setActiveContent] = useState<'table' | 'chart'>('table');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [activeBottomTab, setActiveBottomTab] = useState(0);
  const [hoveredBottomTab, setHoveredBottomTab] = useState<number | null>(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [groupedData, setGroupedData] = useState<any[]>([]);
  const [isManageSopOpen, setIsManageSopOpen] = useState(false);

  const {
    page,
    pageSize,
    sortingInfo,
    appliedFilters,
    summary,
    incidentSummary,
    problemSummary,
    serviceSummary,
    changeSummary,
    data,
    totalCount,
    isLoading,
    isExporting,
    filterOptions,
    originalfilterOptions,
    trendSector,
    trendProgram,
    isChartsLoading,
    isFiltersHydrated,
    mostImpactedApps,
    setPage,
    handlePageNavigation,
    setPageSize,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    liveTile,
    setLiveTile,
    activeParentTile,
    setActiveParentTile,
    setActiveSubTile,
    activeSubTile,
    setGroupBy,
    groupBy,
    setIsGrouped,
    loadMoreChildren,
    loadingGroupId,
    setLoadingGroupId,
    fetchPdfData,
    isDataOpsAdmin,
    loadAlerts,
    getDateParams,
    expandMappedValues,
    filterRelationshipRecords,
    columnFilters,
    handleColumnFilterChange,
    getColumnFilterValues
  } = useUnifiedIncidents();

  const getSopFilterOptions = useCallback((value: any): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return Array.from(
        new Set(
          value
            .map((item) => {
              if (typeof item === 'string') return item;
              return item?.label || item?.value || '';
            })
            .filter(Boolean)
        )
      );
    }

    if (typeof value === 'object') {
      const values = Object.entries(value).flatMap(([key, val]) => {
        if (Array.isArray(val)) return [key, ...val];
        if (typeof val === 'string') return [key, val];
        return [key];
      });

      return Array.from(new Set(values.map((item) => String(item)).filter(Boolean)));
    }

    return [String(value)];
  }, []);

  const getSopFilterValue = useCallback((value: any) => {
    if (!value) return undefined;

    if (Array.isArray(value)) {
      const values = value
        .map((item) => {
          if (typeof item === 'string') return item;
          return item?.value || item?.label || '';
        })
        .filter(Boolean);

      return values.length === 1 ? values[0] : values;
    }

    if (typeof value === 'object') {
      const objectValue = value?.value || value?.label;
      return objectValue ? String(objectValue) : undefined;
    }

    return String(value);
  }, []);

  const getIncidentIdColumnLabel = useCallback(() => {
    if (activeParentTile === 'Problem Tickets') return 'PROBLEM ID';
    if (activeParentTile === 'Service Requests') return 'SERVICE REQUEST ID';
    if (activeParentTile === 'Change Requests') return 'CHANGE REQUEST ID';

    return 'INCIDENT ID';
  }, [activeParentTile]);

    const getMappedSector = (value: any, reverseMap: any) => {
    if (Array.isArray(value)) {
      return value.map(v => reverseMap[v] || v).join(", ");
    }

    return reverseMap[value] || value;
  };

    const reverseSectorMap = useMemo(() => {
      return buildReverseSectorMap(
        filterOptions?.sectorDropdownValues ?? {}
      );
    }, [filterOptions]);

    const renderSectorFormatter = ({ row }: { row: Incident }) => {
      const mapedSector = getMappedSector(row.sector, reverseSectorMap)
      return (
        <Typography
          variant='tableRowFont'
        >
          {mapedSector}
        </Typography>
      )
    };


  const renderDomainFormatter = ({ row }: { row: Incident }) => {
  
    const mappedDomain = mapDomainValue(row.domain!);
  
    return (
      <Typography variant="tableRowFont">
        {mappedDomain}
      </Typography>
    );
  };

  const getProblemStatusColumn = useCallback((autoColumnWidth: any) => {
    const childTile = (activeSubTile && activeSubTile !== 'Problem Tickets') ? activeSubTile : (liveTile !== 'Problem Tickets' ? liveTile : '');
    if (childTile === 'Known Errors') {
      return {
        key: 'Known Errors',
        name: 'Known Errors',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Known Errors'] ?? row?.known_error ?? 'true';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Made SLA') {
      return {
        key: 'SLA State',
        name: 'SLA State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['SLA State'] ?? row?.made_sla ?? 'true';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'RCA') {
      return {
        key: 'Problem State',
        name: 'Problem State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Problem State'] ?? (row?.problem_state === 'Root Cause Analysis' ? 'RCA' : (row?.problem_state || 'RCA'));
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Reactive') {
      return {
        key: 'Source',
        name: 'Source',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Source'] ?? row?.u_source ?? row?.source ?? 'Reactive';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Open') {
      return {
        key: 'Problem State',
        name: 'Problem State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Problem State'] ?? row?.problem_state ?? row?.state ?? '--';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    return {
      key: 'state',
      name: 'State',
      width: autoColumnWidth(140),
      formatter: ({ row }: { row: any }) => {
        const val = row?.['State'] ?? row?.state ?? row?.problem_state ?? '--';
        return <Typography variant="tableRowFont">{String(val)}</Typography>;
      },
    };
  }, [activeSubTile, liveTile]);

  const getChangeStatusColumn = useCallback((autoColumnWidth: any) => {
    const childTile = (activeSubTile && activeSubTile !== 'Change Requests') ? activeSubTile : (liveTile !== 'Change Requests' ? liveTile : '');
    if (childTile === 'Emergency') {
      return {
        key: 'Type',
        name: 'Type',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Type'] ?? row?.type ?? 'Emergency';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'In Progress') {
      return {
        key: 'State',
        name: 'State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['State'] ?? row?.state ?? row?.change_state ?? 'In Progress';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Closed') {
      return {
        key: 'State',
        name: 'State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['State'] ?? row?.state ?? row?.change_state ?? 'Closed';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Not Closed') {
      return {
        key: 'State',
        name: 'State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['State'] ?? row?.state ?? row?.change_state ?? 'Not Closed';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Successful' || childTile === 'Successfull') {
      return {
        key: 'Close Code',
        name: 'Close Code',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Close Code'] ?? row?.close_code ?? 'Successful';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'Unsuccessful') {
      return {
        key: 'Close Code',
        name: 'Close Code',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['Close Code'] ?? row?.close_code ?? 'Unsuccessful';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'SLA Met') {
      return {
        key: 'SLA State',
        name: 'SLA State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['SLA State'] ?? (row?.made_sla === false || row?.made_sla === 'false' ? 'False' : 'True');
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    if (childTile === 'SLA Missed') {
      return {
        key: 'SLA State',
        name: 'SLA State',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['SLA State'] ?? (row?.made_sla === true || row?.made_sla === 'true' ? 'True' : 'False');
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    return {
      key: 'state',
      name: 'State',
      width: autoColumnWidth(140),
      formatter: ({ row }: { row: any }) => {
        const val = row?.['State'] ?? row?.state ?? row?.change_state ?? '--';
        return <Typography variant="tableRowFont">{String(val)}</Typography>;
      },
    };
  }, [activeSubTile, liveTile]);

  const getIncidentStatusColumn = useCallback((autoColumnWidth: any) => {
    const childTile = (activeSubTile && activeSubTile !== 'Incidents') ? activeSubTile : (liveTile !== 'Incidents' ? liveTile : '');
    if (childTile === 'SLA Miss') {
      return {
        key: 'SLA Met',
        name: 'SLA Met',
        width: autoColumnWidth(140),
        formatter: ({ row }: { row: any }) => {
          const val = row?.['SLA Met'] ?? row?.made_sla ?? 'false';
          return <Typography variant="tableRowFont">{String(val)}</Typography>;
        },
      };
    }
    return {
      key: 'incident_state',
      name: 'STATUS',
      width: autoColumnWidth(140),
    };
  }, [activeSubTile, liveTile]);

  const getStatusColumnName = useCallback(() => {
    if (activeParentTile === 'Problem Tickets') {
      const childTile = (activeSubTile && activeSubTile !== 'Problem Tickets') ? activeSubTile : (liveTile !== 'Problem Tickets' ? liveTile : '');
      if (childTile === 'Known Errors') return 'Known Errors';
      if (childTile === 'Made SLA') return 'SLA State';
      if (childTile === 'RCA') return 'Problem State';
      if (childTile === 'Reactive') return 'Source';
      if (childTile === 'Open') return 'Problem State';
      return 'State';
    }
    if (activeParentTile === 'Change Requests') {
      const childTile = (activeSubTile && activeSubTile !== 'Change Requests') ? activeSubTile : (liveTile !== 'Change Requests' ? liveTile : '');
      if (childTile === 'Emergency') return 'Type';
      if (childTile === 'In Progress') return 'State';
      if (childTile === 'Closed') return 'State';
      if (childTile === 'Not Closed') return 'State';
      if (childTile === 'Successful' || childTile === 'Successfull') return 'Close Code';
      if (childTile === 'Unsuccessful') return 'Close Code';
      if (childTile === 'SLA Met') return 'SLA State';
      if (childTile === 'SLA Missed') return 'SLA State';
      return 'State';
    }
    if (activeParentTile === 'Incidents') {
      const childTile = (activeSubTile && activeSubTile !== 'Incidents') ? activeSubTile : (liveTile !== 'Incidents' ? liveTile : '');
      if (childTile === 'SLA Miss') return 'SLA Met';
      return 'STATUS';
    }
    return 'STATUS';
  }, [activeParentTile, activeSubTile, liveTile]);

  const getIncidentSopColumns = useCallback(
    ({
      selectedIds,
      setSelectedIds,
      colors,
      autoColumnWidth,
      snowUrl,
    }: any) => {
      return [
        {
          key: 'selection-checkbox',
          name: 'HeaderCheckboxMapping',
          width: '40px',
          frozen: true,
          formatter: ({ row }: { row: any }) => {
            const incidentId = row?.number;
            const isSelected = selectedIds.includes(incidentId);

            return (
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => {
                  setSelectedIds((prev: string[]) =>
                    isSelected
                      ? prev.filter((selectedId) => selectedId !== incidentId)
                      : [...prev, incidentId]
                  );
                }}
                sx={{ color: '#9CA3AF', '&.Mui-checked': { color: '#39ace7' }, p: 0 }}
              />
            );
          },
        },
        {
          key: 'sopActions',
          name: 'SOP ACTIONS',
          width: 110,
          formatter: ({ row }: { row: any }) => {
            const isSopLinked = row?.sopId || row?.hasSOPLinked || row?.hasSopLinked;

            return (
              <Tooltip title={isSopLinked ? 'SOP Linked' : 'SOP Not Linked'} arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: isSopLinked
                      ? colors.buttons.primary.color
                      : colors.buttons.primary.disabledBg,
                    p: 0.5,
                    cursor: isSopLinked ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (row?.sopLink) {
                      window.open(row.sopLink, '_blank');
                    }
                  }}
                >
                  <DescriptionRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          },
        },
        {
          key: 'number',
          name: getIncidentIdColumnLabel(),
          width: autoColumnWidth(150),
          formatter: ({ row }: { row: any }) => (
            <Typography
              variant="tableRowFont"
              sx={{
                color: 'blue',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => window.open(`${snowUrl}/now/sow/record/incident/${row.sys_id}`, '_blank')}
            >
              {row.number}
            </Typography>
          ),
        },
        { key: 'sopName', name: 'SOP NAME', width: autoColumnWidth(150) },
        { key: 'short_description', name: 'SHORT DESCRIPTION', width: autoColumnWidth(220, 2) },
        ...(activeParentTile === 'Problem Tickets' || activeParentTile === 'Change Requests' || activeParentTile === 'Service Requests'
          ? []
          : [{ key: 'severity', name: 'SEVERITY', width: autoColumnWidth(110) }]),
        { key: 'priority', name: 'PRIORITY', width: autoColumnWidth(110) },
        activeParentTile === 'Problem Tickets'
          ? getProblemStatusColumn(autoColumnWidth)
          : activeParentTile === 'Change Requests'
            ? getChangeStatusColumn(autoColumnWidth)
            : activeParentTile === 'Incidents'
              ? getIncidentStatusColumn(autoColumnWidth)
              : {
                  key: activeParentTile === 'Service Requests'
                    ? 'state'
                    : 'incident_state',
                  name: 'STATUS',
                  width: autoColumnWidth(140)
                },
        { key: 'assignment_group', name: 'ASSIGNMENT GROUP', width: autoColumnWidth(180) },
        { key: 'cmdb_ci', name: 'CI', width: autoColumnWidth(140) },
        { key: 'domain', name: 'Domain', width: autoColumnWidth(140, 1), formatter : renderDomainFormatter },
        { key: 'program', name: 'PROGRAM', width: autoColumnWidth(150) },
        { key: 'sector', name: 'SECTOR', width: autoColumnWidth(140, 1), formatter: renderSectorFormatter },
        { key: 'u_source', name: 'SOURCE', width: autoColumnWidth(130) },
        { key: 'sys_created_on', name: 'CREATED ON', width: autoColumnWidth(170) },
        { key: 'sys_updated_on', name: 'UPDATED ON', width: autoColumnWidth(170) },
        { key: 'sys_created_by', name: 'CREATED BY', width: autoColumnWidth(150) },
        { key: 'sys_updated_by', name: 'UPDATED BY', width: autoColumnWidth(150) },
        { key: 'businessUnit', name: 'BUSINESS UNIT', width: autoColumnWidth(150) },
        { key: 'capability', name: 'CAPABILITY', width: autoColumnWidth(150) },
        { key: 'department', name: 'DEPARTMENT', width: autoColumnWidth(150) },
      ];
    },
    [getIncidentIdColumnLabel, activeParentTile, getProblemStatusColumn, getChangeStatusColumn, getIncidentStatusColumn]
  );

  const fetchIncidentSopRows = useCallback(
    async ({ filters, page, pageSize, sortingInfo, activeTile, searchCursor, direction }: any) => {
      const incidentState = getSopFilterValue(filters?.State || filters?.incident_state);
      const sectorMapping = filterOptions?.sectorDropdownValues || {};

      const expandedSector =
        Array.isArray(filters?.Sector)
          ? filters.Sector.flatMap((sector: string) => {
              return sectorMapping?.[sector] || [sector];
            })
          : [];
      
      const expandedDomain = expandMappedValues(filters?.Domain, DOMAIN_MAP);
      const expandedProgram = expandMappedValues(filters?.Program, PROGRAM_MAP);

      const newFilters = getFiltersForTile(activeTile, appliedFilters, activeParentTile);
      const dynamicFilters = buildFilters({...appliedFilters, ...newFilters });
      const hasFilters = (obj: object) => Object.keys(obj).length > 0;

      const params: any = {
              page,
              pageSize,
              severity: activeTile,
              ...(hasFilters(dynamicFilters) && {
                filter: dynamicFilters,
              }),
      
              ...getItsmSortParams(sortingInfo),
              ...(searchCursor !== undefined && { searchCursor }),
              ...(direction && { direction }),
      };
      
      const transformedParams = {
        ...params,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...params.filter,
            ...(expandedDomain.length > 0 && {
              domain: expandedDomain,
            }),
            ...(expandedProgram.length > 0 && {
              program: expandedProgram,
            }),
                ...(expandedSector.length > 0 && {
              sector: expandedSector,
            }),
        },
      };

      const response: any = await unifiedAlertsService.getITSMData(transformedParams, activeParentTile);
      const responseData = response?.data ?? response;
      const currentChildTile = activeSubTile || liveTile;
      const rawRows = Array.isArray(responseData)
        ? responseData
        : responseData?.data?.docs ?? responseData?.docs ?? responseData?.data ?? [];

      const rows = Array.isArray(rawRows)
        ? rawRows.map((r: any) => {
            if (activeParentTile === 'Problem Tickets') {
              return {
                ...r,
                'Known Errors': r?.known_error ?? 'true',
                'SLA State': r?.made_sla ?? 'true',
                'Problem State': r?.problem_state === 'Root Cause Analysis' ? 'RCA' : (r?.problem_state || (currentChildTile === 'RCA' ? 'RCA' : r?.state || '--')),
                'Source': r?.u_source || r?.source || 'Reactive',
                'State': r?.state || r?.problem_state || '--',
              };
            }
            if (activeParentTile === 'Change Requests') {
              return {
                ...r,
                'Type': r?.type || 'Emergency',
                'State': r?.state || r?.change_state || (currentChildTile === 'In Progress' ? 'In Progress' : currentChildTile === 'Closed' ? 'Closed' : currentChildTile === 'Not Closed' ? 'Not Closed' : '--'),
                'Close Code': r?.close_code || (currentChildTile === 'Unsuccessful' ? 'Unsuccessful' : 'Successful'),
                'SLA State': currentChildTile === 'SLA Missed' ? (r?.made_sla === true || r?.made_sla === 'true' ? 'True' : 'False') : (r?.made_sla === false || r?.made_sla === 'false' ? 'False' : 'True'),
              };
            }
            if (activeParentTile === 'Incidents' && currentChildTile === 'SLA Miss') {
              return {
                ...r,
                'SLA Met': r?.made_sla ?? 'false',
              };
            }
            return r;
          })
        : [];

      return {
        rows: Array.isArray(rows) ? rows : [],
        total:
          response?.pagination?.total ??
          responseData?.pagination?.total ??
          responseData?.total ??
          responseData?.totalDocs ??
          (Array.isArray(rows) ? rows.length : 0),
        pagination: response?.pagination ?? responseData?.pagination,
      };
    },
    [getSopFilterValue, activeParentTile, activeSubTile, liveTile, filterOptions, appliedFilters]
  );

  const getManageSopStepOneLabel = () => {
    if (activeParentTile === 'Problem Tickets') return 'Select Problem Tickets';
    if (activeParentTile === 'Service Requests') return 'Select Service Requests';
    if (activeParentTile === 'Change Requests') return 'Select Change Requests';

    return 'Select Incidents';
  };

  const getManageSopSelectedItemsLabel = () => {
    if (activeParentTile === 'Problem Tickets') return 'Selected Problem Ticket IDs';
    if (activeParentTile === 'Service Requests') return 'Selected Service Request IDs';
    if (activeParentTile === 'Change Requests') return 'Selected Change Request IDs';

    return 'Selected Incident IDs';
  };

  const incidentSopEntityConfig: ManageSopEntityConfig = useMemo(
    () => ({
      title: 'Incidents',
      stepOneLabel: getManageSopStepOneLabel(),
      selectedItemsLabel: getManageSopSelectedItemsLabel(),
      tableKey: `manage-sop-incidents-grid-${activeParentTile}-${activeSubTile || liveTile}`,
      tableType: `ManageSopIncidents_${activeParentTile}_${activeSubTile || liveTile}`,
      defaultColumnNames: [
        'HeaderCheckboxMapping',
        'SOP ACTIONS',
        getIncidentIdColumnLabel(),
        'SOP NAME',
        'SHORT DESCRIPTION',
        ...(activeParentTile === 'Problem Tickets' ? [] : ['SEVERITY']),
        'PRIORITY',
        getStatusColumnName(),
        'ASSIGNMENT GROUP',
      ],
      initialFilters: {
        Domain: [],
        Program: [],
        Sector: [],
        'Alert Pattern': [],
      },
      filterOptions: {
        Domain: getSopFilterOptions(filterOptions?.domain || filterOptions?.domainDropdownValues),
        Program: getSopFilterOptions(filterOptions?.program || filterOptions?.programDropdownValues),
        Sector: getSopFilterOptions(filterOptions?.sector || filterOptions?.sectorDropdownValues),
        'Alert Pattern': [],
      },
      disabledKeys: ['Alert Pattern'],
      defaultSorting: { sortBy: 'sys_updated_on', sortDir: 'DESC' },
      getRowId: (row: any) => row?.number,
      getRowLabel: (row: any) => row?.number,
      getColumns: getIncidentSopColumns,
      fetchRows: fetchIncidentSopRows,
      normalizeFilters: (filters: any) => ({
        Domain: filters?.Domain || [],
        Program: filters?.Program || [],
        Sector: filters?.Sector || [],
        'Alert Pattern': filters?.['Alert Pattern'] || [],
      }),
    }),
    [filterOptions, getSopFilterOptions, getIncidentSopColumns, fetchIncidentSopRows, getIncidentIdColumnLabel, getStatusColumnName, activeParentTile, activeSubTile, liveTile]
  );

  const handleManageSop = () => {
    setIsManageSopOpen(true);
  };

  const getGroupValue = useCallback((row: any, groupByValue: string) => {
    return row?.[groupByValue] ?? row?.groupValue ?? row?.key ?? row?.value ?? row?.name ?? 'Unassigned';
  }, []);

  const getGroupCount = useCallback((row: any, groupByValue: string) => {
    const countField = `${groupByValue}Count`;
    return row?.[countField] ?? row?.totalChildrenCount ?? row?.totalCount ?? row?.count ?? row?.children?.length ?? 0;
  }, []);

  const handleGroupToggle = useCallback((row: any) => {
    if (!groupBy || groupBy === 'none') return;

    setGroupedData(prevData => {
      // Find the index of the parent row
      const rowGroupValue = getGroupValue(row, groupBy);
      const parentIndex = prevData.findIndex((r: any) =>
        r.isParent === true && getGroupValue(r, groupBy) === rowGroupValue
      );

      if (parentIndex === -1) return prevData;

      const currentParent = prevData[parentIndex];

      // IMPORTANT: Get the actual total from the count field
      const countField = `${groupBy}Count`;
      const actualTotalChildren = currentParent[countField] || currentParent.totalChildrenCount || currentParent.totalChildren || currentParent.children?.length || 0;

      const initialShowCount = 50;
      const hasMoreChildren = actualTotalChildren > initialShowCount;

      // Create a new array
      const newRows = [...prevData];

      const parentGroupValue = getGroupValue(currentParent, groupBy);
      if (!currentParent.isExpanded) {
        // EXPAND: Insert children after the parent
        const childrenToShow = (currentParent.children || []).slice(0, initialShowCount);
        const childrenWithFlag = childrenToShow.map((child: any, idx: number) => ({
          ...child,
          [groupBy]: parentGroupValue,
          isChildren: true,
          childIndex: idx,
          parentGroupValue: parentGroupValue
        }));

        // Calculate insert position (right after the parent)
        const insertPosition = parentIndex + 1;

        // Insert children
        newRows.splice(insertPosition, 0, ...childrenWithFlag);

        if (hasMoreChildren && childrenToShow.length < actualTotalChildren) {
          const showMoreRow = {
            id: `show-more-${getGroupValue(currentParent, groupBy)}`,
            isShowMoreRow: true,
            parentRow: {
              ...currentParent,
              totalChildrenCount: actualTotalChildren // ← CRITICAL: Update parentRow with actual total
            },
            currentShowCount: childrenToShow.length,
            totalChildrenCount: actualTotalChildren, // ← Use actual total here
            [groupBy]: getGroupValue(currentParent, groupBy),
            groupBy: groupBy
          };
          newRows.splice(insertPosition + childrenToShow.length, 0, showMoreRow);
        }

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: true,
          showChildrenCount: childrenToShow.length,
          totalChildrenCount: actualTotalChildren // ← Also update parent's totalChildrenCount
        };
      } else {
        // COLLAPSE: Remove only the children and show more rows of this specific parent
        const showChildrenCount = currentParent.showChildrenCount || initialShowCount;
        let rowsToRemove = Math.min(showChildrenCount, actualTotalChildren);

        // Check if there's a show more row
        const hasShowMoreRow = hasMoreChildren && showChildrenCount < actualTotalChildren;
        if (hasShowMoreRow) {
          rowsToRemove += 1;
        }

        // Remove rows starting from position after parent
        newRows.splice(parentIndex + 1, rowsToRemove);

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: false,
          showChildrenCount: initialShowCount
        };
      }

      return newRows;
    });
  }, [groupBy, getGroupValue]);

  const handleShowMoreClick = useCallback(async (showMoreRow: any) => {
    const { parentRow, currentShowCount, groupBy: groupByValue } = showMoreRow;

    // Use the groupBy from the showMoreRow or fallback to the parentRow's groupBy property
    const actualGroupByValue = groupByValue || groupBy;
    const groupValue = getGroupValue(parentRow, actualGroupByValue);

    // Get the actual total from the count field
    const countField = `${actualGroupByValue}Count`;
    const actualTotalChildrenCount = parentRow[countField] || parentRow.totalChildrenCount || parentRow.totalChildren;

    if (!actualGroupByValue || actualGroupByValue === 'none') {
      console.error('Invalid groupBy value:', actualGroupByValue);
      return;
    }

    if (groupValue === undefined || groupValue === null || groupValue === '') {
      console.error('Invalid group value:', groupValue);
      return;
    }

    // Check if we have all children already loaded
    if (currentShowCount >= actualTotalChildrenCount) {
      console.log("All children already loaded");
      return;
    }

    // Set loading state for this specific group
    setLoadingGroupId(`${actualGroupByValue}-${groupValue}`);

    try {
      // Load next batch from API
      const result = await loadMoreChildren(actualGroupByValue, groupValue, currentShowCount, parentRow);

      if (result && result.children && result.children.length > 0) {
        setGroupedData(prevData => {
          const parentIndex = prevData.findIndex((r: any) =>
            getGroupValue(r, actualGroupByValue) === groupValue && r.isParent === true
          );

          if (parentIndex === -1) return prevData;

          const currentParent = prevData[parentIndex];

          // Use the actual total from the parent
          const totalCount = currentParent[countField] || currentParent.totalChildrenCount || currentParent.totalChildren;

          // Find and remove the current "Show More" row
          const showMoreIndex = prevData.findIndex((r: any) =>
            r.id === showMoreRow.id
          );

          if (showMoreIndex === -1) return prevData;

          const newRows = [...prevData];

          const visibleChildKeys = new Set(
            prevData
              .filter((r: any) => r?.isChildren === true && r?.parentGroupValue === groupValue)
              .map((r: any) => r?.sys_id || r?.number || r?.alert_id || r?.id)
              .filter(Boolean)
          );

          const uniqueChildren = result.children.filter((child: any) => {
            const childKey = child?.sys_id || child?.number || child?.alert_id || child?.id;
            return childKey ? !visibleChildKeys.has(childKey) : true;
          });

          if (!uniqueChildren.length) {
            return prevData;
          }

          // Calculate new show count
          const newShowCount = Math.min(currentShowCount + uniqueChildren.length, totalCount);

          // Check if there are more children to load
          const hasMoreChildren = result.hasMore ?? newShowCount < totalCount;

          const childrenWithFlag = uniqueChildren.map((child: any, idx: number) => ({
            ...child,
            isChildren: true,
            childIndex: currentShowCount + idx,
            parentGroupValue: groupValue,
          }));

          // Find the last visible child row for this parent group
          const visibleChildIndexes = prevData
            .map((r: any, index: number) =>
              r?.isChildren === true && r?.parentGroupValue === groupValue ? index : -1
            )
            .filter((index: number) => index !== -1);

          const lastVisibleChildIndex = visibleChildIndexes.length
            ? visibleChildIndexes[visibleChildIndexes.length - 1]
            : parentIndex;

          // Remove the current "Show More" row
          newRows.splice(showMoreIndex, 1);

          // If the Show More row was before the last child index, adjust the index after removal
          const adjustedLastVisibleChildIndex =
            showMoreIndex < lastVisibleChildIndex
              ? lastVisibleChildIndex - 1
              : lastVisibleChildIndex;

          // Insert new children after the last visible child
          const insertPosition = adjustedLastVisibleChildIndex + 1;
          newRows.splice(insertPosition, 0, ...childrenWithFlag);

          // Insert new "Show More" row if there are more children
          if (hasMoreChildren) {
            const newShowMoreRow = {
              id: `show-more-${groupValue}-${newShowCount}`,
              isShowMoreRow: true,
              parentRow: {
                ...currentParent,
                totalChildrenCount: totalCount
              },
              currentShowCount: newShowCount,
              totalChildrenCount: totalCount,
              [actualGroupByValue]: groupValue,
              groupBy: actualGroupByValue,
              isLoading: false
            };
            newRows.splice(insertPosition + uniqueChildren.length, 0, newShowMoreRow);
          }

          // Update parent's data - Merge the children array
          const updatedChildren = [...(currentParent.children || [])];
          uniqueChildren.forEach((newChild: any) => {
            const exists = updatedChildren.some((existingChild: any) =>
              (existingChild.sys_id && newChild.sys_id && existingChild.sys_id === newChild.sys_id) ||
              (existingChild.number && newChild.number && existingChild.number === newChild.number)
            );
            if (!exists) {
              updatedChildren.push(newChild);
            }
          });

          // Update parent state
          newRows[parentIndex] = {
            ...currentParent,
            showChildrenCount: newShowCount,
            children: updatedChildren,
            isExpanded: true,
            totalChildrenCount: totalCount // Ensure parent has correct total
          };

          return newRows;
        });
      }
    } catch (error) {
      console.error('Error loading more children:', error);
    } finally {
      setLoadingGroupId(null);
    }
  }, [loadMoreChildren, groupBy, getGroupValue, setLoadingGroupId]);

  // In the useEffect that sets groupedData from tableData
  useEffect(() => {
    if (data && data.length > 0) {
      const isAlreadyGrouped = groupBy && groupBy !== 'none' && (data[0]?.isParent === true || Array.isArray(data[0]?.children));

      if (isAlreadyGrouped) {
        const resetGroupedData = data.map((group: any) => {
          const groupValue = getGroupValue(group, groupBy);
          const countField = `${groupBy}Count`;
          const totalChildrenCount = getGroupCount(group, groupBy);

          return {
            ...group,
            [groupBy]: groupValue,
            [countField]: group?.[countField] ?? totalChildrenCount,
            isParent: true,
            isExpanded: false,
            showChildrenCount: 50,
            // Store the actual total children count from the API response if available
            // This should come from the API response in the group object
            totalChildrenCount,
            children: Array.isArray(group?.children) ? group.children : [],
          };
        });
        setIsGrouped(true);
        setGroupedData(resetGroupedData);
      } else {
        setIsGrouped(false);
        setGroupedData(data);
      }
    } else {
      setGroupedData([]);
      setIsGrouped(false);
    }
  }, [data, groupBy, getGroupValue, getGroupCount, setIsGrouped]);

  const allDiff = summary?.ALL?.difference ?? null;
  const totalSubtitle = allDiff !== null ? `${Math.abs(allDiff)} from last hour` : undefined;
  const totalTrend: 'up' | 'down' | 'neutral' =
    summary?.ALL?.trend === 'INCREASED'
      ? 'up'
      : summary?.ALL?.trend === 'DECREASED'
        ? 'down'
        : 'neutral';

  const getTimeRangeLabel = () => {
    const sessionRange = sessionStorage.getItem('unifiedAlertsDateRangeType');
    const effectiveRange = sessionRange || appliedFilters.timeRange;

    if (effectiveRange === 'custom') {
      const start = appliedFilters.startDate || sessionStorage.getItem('unifiedAlertsStartDate');
      const end = appliedFilters.endDate || sessionStorage.getItem('unifiedAlertsEndDate');

      if (start && end) {
        const startDate = moment(start).tz(TIMEZONE_MAP.CST).format('MM-DD-YYYY');
        const endDate = moment(end).tz(TIMEZONE_MAP.CST).format('MM-DD-YYYY');
        return `${startDate} – ${endDate}`;
      }

      return 'Custom Range';
    }

    const optionLabel = TIME_RANGE_OPTIONS.find(o => o.value === effectiveRange)?.label;

    if (optionLabel) return optionLabel;

    const labelMap: Record<string, string> = {
      '5m': 'Last 5 Minutes',
      '10m': 'Last 10 Minutes',
      '15m': 'Last 15 Minutes',
      '30m': 'Last 30 Minutes',
      '1h': 'Last 1 Hour',
      'today': 'Today',
      '24h': 'Last 24 Hours',
      '7d': 'Last 7 Days',
      '30d': 'Last 30 Days',
      '90d': 'Last 90 Days',
    };

    return labelMap[effectiveRange] || '';
  };

  const timeRange = isFiltersHydrated ? getTimeRangeLabel() : '';

  const chartContent = [
    <TrendBySector key="sector" series={trendSector?.series || []} stats={{ ...(trendSector?.stats || {}), totalSubtitle, totalTrend }} isLoading={isChartsLoading} type={liveTile !== activeParentTile ? `${activeParentTile} - ${liveTile}` : activeParentTile} />,
    <TrendByProgram key="program" series={trendProgram?.series || []} stats={{ ...(trendProgram?.stats || {}), totalSubtitle, totalTrend }} isLoading={isChartsLoading} type={liveTile !== activeParentTile ? `${activeParentTile} - ${liveTile}` : activeParentTile} />,
    <MostImpactedApp key="impacted" data={mostImpactedApps} isLoading={isChartsLoading} type={liveTile !== activeParentTile ? `${activeParentTile} - ${liveTile}` : activeParentTile} />,
  ];

  const isScreenshotOnlyChartSelected = activeContent === 'chart' && CHART_CARDS.some((card) => card.key === activeBottomTab);

  const subTiles = SUB_TILES_BY_PARENT[activeParentTile] || [];

  const handleParentTileClick = (key: string) => {
    // Ultimate truth
    setLiveTile(key)
    setActiveParentTile(key);

    setActiveContent('table');

    const nextSubTiles = SUB_TILES_BY_PARENT[key] || [];
    const nextActiveSubTile = nextSubTiles[0]?.key;

    if (nextActiveSubTile) {
      setActiveSubTile(nextActiveSubTile);
      handleTileClick(nextActiveSubTile);
      return;
    }

    setActiveSubTile('');
    handleTileClick(key);
  };

  const handleSubTileClick = (key: string) => {
    // Ultimate truth
    setLiveTile(key)

    setActiveSubTile(key);
    setActiveContent('table');
    handleTileClick(key);
  };

  const handleDownloadPDF = async () => {
    if (isPdfGenerating) return;
    setIsPdfGenerating(true);
    try {
      const pdfData = await fetchPdfData();

      // ── Column label helpers ─────────────────────────────────
      let numberLabel = 'INCIDENT ID';
      if (activeParentTile === 'Problem Tickets') {
        numberLabel = 'PROBLEM ID';
      } else if (activeParentTile === 'Service Requests') {
        numberLabel = 'SERVICE REQUEST ID';
      } else if (activeParentTile === 'Change Requests') {
        numberLabel = 'CHANGE REQUEST ID';
      }

      const statusKey =
        activeParentTile === 'Incidents' ? 'incident_state' : 'state';

      // ── Formatters ──────────────────────────────────────
      const fmtPriority = (row: any): string => {
        const v = row?.priority ?? '';
        if (v === '3 - Moderate') return 'P3';
        if (v === '4 - Standard') return 'P4';
        if (v === '5 – Regular') return 'P5';
        return v || '--';
      };

      const fmtDateTime = (field: string) => (row: any): string => {
        const v = row?.[field];
        if (!v) return '--';
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('en-US');
      };

      const fmtBooleanStr = (field: string) => (row: any): string => {
        const val = row?.[field];
        if (val === true || val === 'true') return 'Yes';
        if (val === false || val === 'false') return 'No';
        return '--';
      };

      // ── Column definitions (dynamic per parentTile) ──────────
      const allPdfColumnsMap: Record<string, PdfColumn> = {
        'number': { key: 'number', label: numberLabel, weight: 16, formatter: r => r?.number || '--' },
        'short_description': { key: 'short_description', label: 'TITLE', weight: 30, formatter: r => String(r?.short_description ?? '--').slice(0, 60) },
        'priority': { key: 'priority', label: 'PRIORITY', weight: 12, formatter: fmtPriority, isStatus: true },
        [statusKey]: { key: statusKey, label: 'STATUS', weight: 14, formatter: r => r?.[statusKey] || '--' },
        'assignment_group': { key: 'assignment_group', label: 'ASSIGNMENT GROUP', weight: 24, formatter: r => r?.assignment_group || '--' },
        'sys_updated_on': { key: 'sys_updated_on', label: 'LAST UPDATED', weight: 20, formatter: fmtDateTime('sys_updated_on') },
        'comments': { key: 'comments', label: 'COMMENTS', weight: 22, formatter: r => r?.comments || '--' },
        'category': { key: 'category', label: 'CATEGORY', weight: 14, formatter: r => r?.category || '--' },
        'subcategory': { key: 'subcategory', label: 'SUB CATEGORY', weight: 14, formatter: r => r?.subcategory || '--' },
        'cmdb_ci': { key: 'cmdb_ci', label: 'CI', weight: 14, formatter: r => r?.cmdb_ci || '--' },
        'opened_at': { key: 'opened_at', label: 'OPEN DATE', weight: 20, formatter: fmtDateTime('opened_at') },
        'opened_by': { key: 'opened_by', label: 'OPENED FOR', weight: 14, formatter: r => r?.opened_by || '--' },
        'impact': { key: 'impact', label: 'IMPACT', weight: 11, formatter: r => r?.impact || '--' },
        'urgency': { key: 'urgency', label: 'URGENCY', weight: 11, formatter: r => r?.urgency || '--' },
        'known_error': { key: 'known_error', label: 'KNOWN ERROR', weight: 13, formatter: fmtBooleanStr('known_error') },
        'u_source': { key: 'u_source', label: 'SOURCE', weight: 14, formatter: r => r?.u_source || '--' },
        'made_sla': { key: 'made_sla', label: 'MADE SLA', weight: 12, formatter: fmtBooleanStr('made_sla') },
        'resolution_slaHasBreached': { key: 'resolution_slaHasBreached', label: 'SLA Resolution Miss', weight: 11, formatter: r => r?.resolution_slaHasBreached || '--' },
        'response_slaHasBreached': { key: 'response_slaHasBreached', label: 'SLA Response Miss', weight: 11, formatter: r => r?.response_slaHasBreached || '--' },
      };

      // ── Build name→key mapping from table column definitions ──
      // Mirrors the columns array in IncidentsTable.tsx
      const tableColNameToKey: Record<string, string> = {
        [numberLabel]: 'number', 'TITLE': 'short_description', 'PRIORITY': 'priority',
        'STATUS': statusKey, 'ASSIGNMENT GROUP': 'assignment_group',
        'LAST UPDATED': 'sys_updated_on', 'COMMENTS': 'comments',
        'CATEGORY': 'category', 'SUB CATEGORY': 'subcategory', 'CI': 'cmdb_ci',
        'OPEN DATE': 'opened_at', 'OPENED FOR': 'opened_by',
        'IMPACT': 'impact', 'URGENCY': 'urgency',
        'KNOWN ERROR': 'known_error', 'SOURCE': 'u_source', 'MADE SLA': 'made_sla',
        'Department': 'department', 'Capability': 'capability', 'Vendor': 'vendor', 'Severity': 'severity',
        'SLA Response Miss' : 'response_slaHasBreached', 
        'SLA Resolution Miss' : 'resolution_slaHasBreached'
      };

      // ── Read user's column preferences from localStorage ──
      const tableType = `UnifiedIncidentsSnow_${activeParentTile}`;
      const storedPref = localStorage.getItem(`${tableType}_user_col_pref`);
      let preferredColumnNames: string[] | null = null;
      if (storedPref && storedPref !== 'undefined') {
        try {
          preferredColumnNames = JSON.parse(storedPref);
        } catch { preferredColumnNames = null; }
      }

      // Fall back to default columns if no user preference is stored
      // (Excludes ACTIONS as it is a UI-only interactive column)
      const defaultPdfColumns = [
        numberLabel, 'TITLE', 'PRIORITY', 'STATUS', 'ASSIGNMENT GROUP', 'LAST UPDATED',
        'COMMENTS'];
      const columnNames = (preferredColumnNames && Array.isArray(preferredColumnNames) && preferredColumnNames.length > 0)
        ? preferredColumnNames.filter(name => name !== 'ACTIONS')
        : defaultPdfColumns;

      // ── All possible PDF column definitions, keyed by data field ──
      const pdfColumns: PdfColumn[] = columnNames
        .map(name => {
          const dataKey = tableColNameToKey[name];
          if (!dataKey) return undefined;
          const pdfCol = allPdfColumnsMap[dataKey];
          if (!pdfCol) return undefined;
          return { ...pdfCol, label: name };
        })
        .filter((col): col is PdfColumn => col !== undefined);

      await generateDashboardPdf({
        columns: pdfColumns,
        allData: pdfData,
        dashboardTitle: 'Incidents Summary Dashboard',
        tableTitle: liveTile,
        totalElements: totalCount,
        topKpiElementId: ['ua-top-tabs', 'ua-sub-tabs'],
        fileNamePrefix: `IncidentsDashboard_${liveTile}`,
      });
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleScreenCapture = async () => {
    try {
      setScreenCapturing(true)

      const element = document.getElementById('ua-capture-area')
      if (!element) return

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        ignoreElements: (el: any) => ['exclude-camera', 'exclude-export'].includes(el.id)
      })

      const image = canvas.toDataURL('image/png')

      setPreviewImage(image)
      setOpenPreview(true)
    } catch (err) {
      console.error('Screenshot failed', err)
    } finally {
      setScreenCapturing(false)
    }
  }


  const getSummary = (activeParentTile: string, summaries: any) => {
    const map: Record<string, any> = {
      "Problem Tickets": summaries.problemSummary,
      "Service Requests": summaries.serviceSummary,
      "Change Requests": summaries.changeSummary,
    };

    return map[activeParentTile] ?? summaries.incidentSummary;
  };


  const getIsLoading = (activeParentTile: string, summaries: Record<string, any>) => {
    const map: Record<string, any> = {
      "Problem Tickets": summaries.problemSummary,
      "Service Requests": summaries.serviceSummary,
      "Change Requests": summaries.changeSummary,
    };

    const selectedSummary = map[activeParentTile] ?? summaries.incidentSummary;

    return selectedSummary === null;
  };

   // The drawer narrows its own option lists while values are being selected (e.g. Assignment Group -> Service Offering).
   // It works on this copy so the dashboard does not reload before Apply.
   const [drawerFilterOptions, setDrawerFilterOptions] = useState(filterOptions);
   useEffect(() => {
      setDrawerFilterOptions(filterOptions);
   }, [filterOptions]);

   useEffect(() => {
      setRightContextContent(
        <Box display="flex" alignItems="center" gap={2}>
          <OpsAdvancedFilterPanel
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            onApply={handleApplyFilters}
            onSaveDefault={handleSaveDefaultFilters}
            onResetDefault={handleResetDefaultFilters}
            currentFilters={appliedFilters}
            filterOptions={drawerFilterOptions}
            setFilterOptions={setDrawerFilterOptions}
            originalfilterOptions={originalfilterOptions}
            activeTile={liveTile}
            activeParentTile={activeParentTile}
            filterRelationshipRecords={filterRelationshipRecords}
          />
        </Box>,
      );
  
      return () => {
        setRightContextContent(null);
      };
    }, [appliedFilters, drawerFilterOptions, liveTile, activeParentTile]);
  return (
    <PageWrapper sx={{ minHeight: 'calc(100vh - 120px)', backgroundColor: p.backgroundLight.sideNavBgHover, position: 'relative' }}>

      <Box id="ua-main" sx={{ pt: 1, position: 'relative', backgroundColor: p.backgroundLight.sideNavBgHover }}>
        <Box id="ua-capture-area">
          <Box id="ua-top-tabs" sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, backgroundColor: p.backgroundLight.secondary }}>
            <SeverityTiles
              type='ITSM'
              summary={summary as any}
              activeTile={liveTile}
              parentTile={activeParentTile}
              onTileClick={handleParentTileClick}
              selectedBgColor="#ffffff"
              defaultBgColor="#103771"
              selectedTextColor="#103771"
              defaultTextColor="#ffffff"
              isLoading={summary === null || !incidentSummary || !problemSummary || !serviceSummary || !changeSummary}
              timePeriod={timeRange}
              sx={{ width: 'calc(100% - 56px) !important' }}
            />
            <Box
              sx={{
                width: '56px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <CaptureExportControls
                onScreenshot={handleScreenCapture}
                screenCapturing={screenCapturing}
                onExport={handleExport}
                isExporting={isExporting}
                showExport={!isScreenshotOnlyChartSelected}
                isVisible={true}
                type="incident"
                exportTooltip="Max 200K records will be exported at a Time."
              />
            </Box>
          </Box>

          {!!subTiles.length && (
            <Box id="ua-sub-tabs" sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, p: 2, backgroundColor: p.backgroundLight.secondary }}>
              <SeverityTiles
                summary={getSummary(activeParentTile, {
                  incidentSummary,
                  problemSummary,
                  serviceSummary,
                  changeSummary
                })}
                type={activeSubTile ? activeParentTile : undefined}
                activeTile={liveTile}
                onTileClick={handleSubTileClick}
                isLoading={getIsLoading(activeParentTile, {
                  incidentSummary,
                  problemSummary,
                  serviceSummary,
                  changeSummary
                })}
                timePeriod={timeRange}
                selectedBgColor="#ffffff"
                defaultBgColor="#EFF6FF"
                selectedTextColor="#103771"
                defaultTextColor="#103771"
                sx={{ width: '100% !important' }}
              />
            </Box>
          )}

          <Box id="ua-tab-content" sx={{ backgroundColor: p.backgroundLight.secondary, p: 3, pt: activeContent === 'chart' ? 5 : 3, position: 'relative', minHeight: '300px' }}>
            {activeContent === 'table' && (
              <Typography variant="h4" sx={{ color: p.primary.main, fontWeight: 500 }}>
                {activeParentTile}{liveTile !== activeParentTile ? ` - ${liveTile}` : ''}
              </Typography>
            )}

            {activeContent === 'table' ? (
              <IncidentsTable
                key={activeParentTile}
                incidents={groupedData}
                total={totalCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageNavigation}
                filterOptions={filterOptions}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                onSorting={setSortingInfo}
                sortingInfo={sortingInfo}
                isLoading={isLoading}
                parentTile={activeParentTile}
                onGroupByChange={setGroupBy}
                groupByList={groupByList}
                groupBy={groupBy}
                includeGroupByMenuColumns={includeGroupByMenuColumns}
                onGroupToggle={handleGroupToggle}
                onShowMoreClick={handleShowMoreClick}
                loadingGroupId={loadingGroupId}
                isDataOpsAdmin={isDataOpsAdmin}
                onManageSop={handleManageSop}
                columnFilters={columnFilters}
                onColumnFilterChange={handleColumnFilterChange}
                getColumnFilterValues={getColumnFilterValues}
                liveTile={liveTile}
              />
            ) : (
              chartContent[activeBottomTab]
            )}
          </Box>
        </Box>

        <ManageSopDialog
          open={isManageSopOpen}
          onClose={() => setIsManageSopOpen(false)}
          type="OPS_PULSE"
          activeTile={liveTile}
          entityConfig={incidentSopEntityConfig}
          onSopAttached={() => {
            loadAlerts(getDateParams());
          }}
        />

        <Box id="ua-bottom-tabs" sx={{ display: 'flex', gap: 0.5, width: '66.67%' }}>
          {CHART_CARDS.map((card) => {
            const isActive = activeContent === 'chart' && activeBottomTab === card.key;
            const isHovered = hoveredBottomTab === card.key;
            const { Icon } = card;
            const iconColor = isActive || isHovered ? p.primary.main : p.white.main;
            return (
              <Box
                key={card.key}
                onClick={() => { setActiveBottomTab(card.key); setActiveContent('chart'); }}
                onMouseEnter={() => setHoveredBottomTab(card.key)}
                onMouseLeave={() => setHoveredBottomTab(null)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: 0,
                  py: 2.5,
                  px: 2,
                  cursor: 'pointer',
                  backgroundColor: isActive || isHovered ? p.white.main : p.primary.main,
                  transition: 'background-color 0.2s ease',
                }}
              >
                <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <Icon color={iconColor} />
                </Box>
                <Typography variant="body1" sx={{ color: iconColor, fontWeight: 500, textAlign: 'right', lineHeight: 1.2 }}>
                  {card.label}
                </Typography>
                <Typography variant="body2" sx={{ color: iconColor, textAlign: 'right' }}>
                  {timeRange}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <ImagePreview
        open={openPreview}
        image={previewImage}
        fileName={`screenshot_${liveTile}_${formattedDate}`}
        onClose={() => setOpenPreview(false)}
        title={'Incidents Summary Dashboard'}
        hasDownloadPDFButton
        handleDownloadPDF={handleDownloadPDF}
        isPdfGenerating={isPdfGenerating}
      />
    </PageWrapper>
  );
};

export default SnowUnifiedIncidentsDashboard;